import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Send, CheckCircle, Loader2, ArrowRight
} from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function for button icon based on status
const getSubmitIcon = (status) => {
  if (status === 'loading') return <Loader2 className="w-4 h-4 animate-spin" />;
  if (status === 'success') return <CheckCircle className="w-4 h-4" />;
  return <Send className="w-4 h-4" />;
};

// Footer navigation links
const footerLinks = {
  company: [
    { name: 'About Us', href: '#about' },
    { name: 'Our Services', href: '#services' },
    { name: 'Our Values', href: '#values' },
    { name: 'Global Impact', href: '#impact' },
  ],
  resources: [
    { name: 'Blog', href: '/blog' },
    { name: 'Case Studies', href: '#' },
    { name: 'Documentation', href: '#' },
    { name: 'FAQs', href: '#' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '#' },
    { name: 'Terms of Service', href: '#' },
    { name: 'Cookie Policy', href: '#' },
  ],
};

// Newsletter subscription component
function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      setStatus('success');
      setMessage(response.data.message || 'Successfully subscribed!');
      setEmail('');
      
      // Reset after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Something went wrong. Please try again.');
      
      // Reset after 3 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 3000);
    }
  };

  return (
    <div className="newsletter-signup">
      <h4 className="font-heading font-semibold text-white mb-2">
        Stay Updated
      </h4>
      <p className="text-white/50 text-sm mb-4">
        Get the latest AI insights and updates delivered to your inbox.
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={status === 'loading' || status === 'success'}
              className="pl-10 bg-ink-2 border-line-1 text-stark placeholder:text-stark-3 focus:border-white"
              data-testid="newsletter-email-input"
            />
          </div>
          <Button
            type="submit"
            disabled={status === 'loading' || status === 'success' || !email}
            className="bg-white hover:bg-stark-2/95 text-black px-4"
            data-testid="newsletter-submit-btn"
          >
            {getSubmitIcon(status)}
          </Button>
        </div>
        
        {message && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-sm ${status === 'success' ? 'text-green-400' : 'text-red-400'}`}
          >
            {message}
          </motion.p>
        )}
      </form>
      
      <p className="text-white/30 text-xs mt-3">
        No spam, unsubscribe anytime.
      </p>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="relative bg-ink border-t border-line-1" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <a href="/" className="flex items-center gap-3 mb-4">
              <img 
                src="/logo.png" 
                alt="ServeNetics" 
                className="h-12 w-auto rounded-lg"
              />
              <span className="font-heading font-bold text-xl text-white">
                ServeNetics
              </span>
            </a>
            <p className="text-white/60 text-sm mb-6 max-w-sm">
              Where Artificial Intelligence meets Purpose — building scalable solutions that transform businesses, empower communities, and unlock human potential globally.
            </p>
            
            {/* Newsletter */}
            <NewsletterSignup />
          </div>
          
          {/* Company links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-stark-2 hover:text-stark transition-colors text-sm flex items-center gap-1 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Resources links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-stark-2 hover:text-stark transition-colors text-sm flex items-center gap-1 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Legal links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-stark-2 hover:text-stark transition-colors text-sm flex items-center gap-1 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Copyright */}
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} ServeNetics. All rights reserved.
            </p>
            
            {/* Tagline */}
            <p className="text-white/50 text-sm font-mono">
              Serving Humanity Through Innovation
            </p>
          </div>
        </div>
      </div>
      
      {/* Decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-line-2 to-transparent" />
    </footer>
  );
}

export default Footer;
